/**
 * EAManagerTab Component
 * Main orchestrator for the EA Assistant feature
 *
 * User Flow:
 * 1. File List → 2. Select File → 3. Consent Modal → 4. Analysis (SignalR progress) → 5. Results → 6. Diff View → 7. Save
 *
 * Features:
 * - Two-panel layout: File list (left) + Content area (right)
 * - Consent modal before code analysis (CRITICAL)
 * - Real-time progress updates via SignalR
 * - Smooth transitions between views
 * - Integrated error handling
 * - EA Chat Drawer for contextual Q.AI conversations
 */

import React, { useCallback, useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Chip
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import useEAAssistant, { EA_VIEWS } from '../../../hooks/useEAAssistant';
import useEAAsyncAnalysis, { ANALYSIS_STATE } from '../../../hooks/useEAAsyncAnalysis';
import EAFileList from './EAFileList';
import CostEstimateDialog from './CostEstimateDialog';
import EAConsentModal from './EAConsentModal';
import EAProgressPanel from './EAProgressPanel';
import EAAnalysisResults from './EAAnalysisResults';
import EADiffViewer from './EADiffViewer';
import EAChatDrawer from './EAChatDrawer';
import DuplicateEAModal from './DuplicateEAModal';
import MQLLibraryDropdown from './MQLLibraryDropdown';
import { getFileTypeConfig } from '../../../api/qbridgeEaApi';
import { buildEAContextFromAnalysis } from '../../../context/EAAnalysisContext';

/**
 * File Details Panel - Shows when a file is selected but not yet analyzed
 */
const FileDetailsPanel = ({
  file,
  onAnalyze,
  onAskQ,
  isLoadingContent,
  error
}) => {
  if (!file) return null;

  const fileConfig = getFileTypeConfig(file.file_type);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        padding: { xs: '32px', md: '48px' },
        textAlign: 'center'
      }}
    >
      {/* File Type Icon */}
      <Box
        sx={{
          width: 80,
          height: 80,
          borderRadius: '20px',
          background: `linear-gradient(135deg, ${fileConfig.color}25 0%, ${fileConfig.color}08 100%)`,
          border: `2px solid ${fileConfig.color}30`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 3,
          boxShadow: `0 8px 32px ${fileConfig.color}15`
        }}
      >
        <Typography
          sx={{
            color: fileConfig.color,
            fontSize: '1.75rem',
            fontWeight: 800
          }}
        >
          {fileConfig.label}
        </Typography>
      </Box>

      {/* File Name */}
      <Typography
        sx={{
          color: '#FFFFFF',
          fontSize: '1.25rem',
          fontWeight: 600,
          fontFamily: "'JetBrains Mono', monospace",
          mb: 1,
          wordBreak: 'break-all',
          maxWidth: '100%',
          letterSpacing: '-0.01em'
        }}
      >
        {file.name}
      </Typography>

      {/* File Stats */}
      <Typography
        sx={{
          color: 'rgba(255, 255, 255, 0.5)',
          fontSize: '0.9375rem',
          mb: 4
        }}
      >
        {file.line_count?.toLocaleString() || '?'} lines
        {(typeof file.size_kb === 'number') && ` • ${file.size_kb.toFixed(1)} KB`}
      </Typography>

      {/* Error Message */}
      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 3,
            backgroundColor: 'rgba(239, 68, 68, 0.1)',
            color: '#EF4444',
            border: '1px solid rgba(239, 68, 68, 0.2)',
            maxWidth: '360px'
          }}
        >
          {error}
        </Alert>
      )}

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, width: '100%', maxWidth: '320px' }}>
        {/* Primary CTA: Analyze */}
        <Button
          onClick={onAnalyze}
          disabled={isLoadingContent}
          startIcon={isLoadingContent ? <CircularProgress size={18} /> : <AutoAwesomeIcon />}
          sx={{
            background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)',
            color: '#FFFFFF',
            textTransform: 'none',
            fontWeight: 600,
            padding: '14px 24px',
            borderRadius: '12px',
            fontSize: '0.9375rem',
            '&:hover': {
              background: 'linear-gradient(135deg, #6d28d9 0%, #9333ea 100%)'
            },
            '&.Mui-disabled': {
              background: 'rgba(124, 58, 237, 0.3)',
              color: 'rgba(255, 255, 255, 0.5)'
            }
          }}
        >
          {isLoadingContent ? 'Loading...' : 'Analyze with Q.AI'}
        </Button>

        {/* Secondary: Ask Q */}
        <Button
          onClick={onAskQ}
          sx={{
            backgroundColor: 'rgba(255, 255, 255, 0.06)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            color: 'rgba(255, 255, 255, 0.9)',
            textTransform: 'none',
            fontWeight: 500,
            padding: '12px 24px',
            borderRadius: '12px',
            fontSize: '0.875rem',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              borderColor: 'rgba(255, 255, 255, 0.2)'
            }
          }}
        >
          Ask Q about this EA
        </Button>
      </Box>

      {/* Existing QAI Version Badge */}
      {file.has_qai_version && (
        <Chip
          label="Has Q.AI version"
          size="small"
          sx={{
            mt: 3,
            backgroundColor: 'rgba(39, 181, 109, 0.15)',
            color: '#27B56D',
            fontWeight: 500,
            border: '1px solid rgba(39, 181, 109, 0.3)'
          }}
        />
      )}
    </Box>
  );
};

/**
 * Analysis Loading Panel - Shows during analysis
 */
const AnalyzingPanel = ({ file }) => {
  const fileConfig = getFileTypeConfig(file?.file_type || 'expert');

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        padding: '40px',
        textAlign: 'center'
      }}
    >
      {/* Animated Loading */}
      <Box
        sx={{
          position: 'relative',
          width: 80,
          height: 80,
          mb: 3
        }}
      >
        <CircularProgress
          size={80}
          thickness={2}
          sx={{
            color: '#7c3aed',
            position: 'absolute',
            top: 0,
            left: 0
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)'
          }}
        >
          <AutoAwesomeIcon
            sx={{
              color: '#a855f7',
              fontSize: '2rem',
              animation: 'pulse 1.5s ease-in-out infinite',
              '@keyframes pulse': {
                '0%, 100%': { opacity: 0.6, transform: 'scale(1)' },
                '50%': { opacity: 1, transform: 'scale(1.1)' }
              }
            }}
          />
        </Box>
      </Box>

      <Typography
        sx={{
          color: '#FFFFFF',
          fontSize: '1.125rem',
          fontWeight: 600,
          mb: 1
        }}
      >
        Q.AI is analyzing your code
      </Typography>

      <Typography
        sx={{
          color: 'rgba(255, 255, 255, 0.5)',
          fontSize: '0.875rem',
          maxWidth: '280px'
        }}
      >
        Reading {file?.name}, understanding the trading logic, and checking for issues...
      </Typography>

      {/* Progress Steps (mock) */}
      <Box
        sx={{
          mt: 4,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          alignItems: 'flex-start'
        }}
      >
        {[
          { label: 'Loading source code', done: true },
          { label: 'Analyzing trading logic', done: false },
          { label: 'Checking for issues', done: false },
          { label: 'Generating suggestions', done: false }
        ].map((step, index) => (
          <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 16,
                height: 16,
                borderRadius: '50%',
                backgroundColor: step.done ? '#27B56D' : 'rgba(124, 58, 237, 0.3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {step.done && (
                <Typography sx={{ color: '#FFFFFF', fontSize: '0.625rem' }}>✓</Typography>
              )}
            </Box>
            <Typography
              sx={{
                color: step.done ? 'rgba(255, 255, 255, 0.8)' : 'rgba(255, 255, 255, 0.4)',
                fontSize: '0.8125rem'
              }}
            >
              {step.label}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

/**
 * Empty State Panel - When no file is selected
 * P9-C: Now includes MQL Library dropdown
 */
const EmptyStatePanel = ({
  qbridgeOnline,
  libraryItems,
  libraryGrouped,
  libraryByType,
  isLoadingLibrary,
  libraryError,
  onLibrarySelect,
  onLibraryRefresh
}) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100%',
      padding: { xs: '32px', md: '48px' },
      textAlign: 'center'
    }}
  >
    {/* Icon Container */}
    <Box
      sx={{
        width: 80,
        height: 80,
        borderRadius: '20px',
        background: 'linear-gradient(135deg, rgba(124, 58, 237, 0.2) 0%, rgba(168, 85, 247, 0.1) 100%)',
        border: '2px solid rgba(124, 58, 237, 0.25)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        mb: 3,
        boxShadow: '0 8px 32px rgba(124, 58, 237, 0.15)'
      }}
    >
      <AutoAwesomeIcon sx={{ color: '#a855f7', fontSize: '2.25rem' }} />
    </Box>

    {/* Title */}
    <Typography
      sx={{
        color: '#FFFFFF',
        fontSize: '1.25rem',
        fontWeight: 700,
        mb: 1.5,
        letterSpacing: '-0.01em'
      }}
    >
      Select an EA to Analyze
    </Typography>

    {/* Description */}
    <Typography
      sx={{
        color: 'rgba(255, 255, 255, 0.6)',
        fontSize: '0.9375rem',
        maxWidth: '320px',
        lineHeight: 1.6,
        mb: 3
      }}
    >
      {qbridgeOnline
        ? 'Choose a file from the list to get AI-powered analysis and improvement suggestions.'
        : 'Start QBridge to view your MT5 Expert Advisors, Indicators, and Scripts.'}
    </Typography>

    {/* P9-C: MQL Library Dropdown */}
    <Box sx={{ mb: 3 }}>
      <MQLLibraryDropdown
        libraryItems={libraryItems}
        grouped={libraryGrouped}
        byType={libraryByType}
        onSelect={onLibrarySelect}
        isLoading={isLoadingLibrary}
        error={libraryError}
        onRefresh={onLibraryRefresh}
        disabled={false}
      />
    </Box>

    {/* Feature Pills */}
    {qbridgeOnline && (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center', maxWidth: '360px' }}>
        {['Bug Detection', 'Code Quality', 'Performance Tips', 'Risk Analysis'].map((feature) => (
          <Box
            key={feature}
            sx={{
              padding: '6px 14px',
              borderRadius: '20px',
              backgroundColor: 'rgba(124, 58, 237, 0.1)',
              border: '1px solid rgba(124, 58, 237, 0.2)',
              fontSize: '0.8125rem',
              color: 'rgba(255, 255, 255, 0.7)',
              fontWeight: 500
            }}
          >
            {feature}
          </Box>
        ))}
      </Box>
    )}
  </Box>
);

/**
 * EAManagerTab Component
 * @param {string} token - JWT authentication token
 * @param {number} balance - Current token balance
 * @param {function} onAskQ - Handler to ask Q.AI a question (integrates with chat) - DEPRECATED: Use drawer instead
 * @param {function} onBuyTokens - Handler to open pricing modal
 * @param {string} userId - User ID for P9-B/C features (optional, falls back to localStorage)
 */
const EAManagerTab = ({
  token,
  balance = 0,
  onAskQ,
  onBuyTokens,
  userId: propUserId
}) => {
  // Get userId from prop or localStorage
  const userId = React.useMemo(() => {
    if (propUserId) return propUserId;
    try {
      const userStr = localStorage.getItem('qai_user') || localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        return user?.id || user?.userId || null;
      }
    } catch {
      // Ignore parse errors
    }
    return null;
  }, [propUserId]);
  // ═══════════════════════════════════════════════════════════════════════════
  // EA CHAT DRAWER STATE
  // ═══════════════════════════════════════════════════════════════════════════
  const [chatDrawerOpen, setChatDrawerOpen] = useState(false);
  const [chatInitialPrompt, setChatInitialPrompt] = useState(null);
  const [eaAnalysisContext, setEaAnalysisContext] = useState(null);

  const {
    // QBridge
    qbridgeOnline,
    checkQBridge,

    // Files
    files,
    fileSummary,
    isLoadingFiles,
    filesError,
    refreshFiles,

    // Analysis history
    analyzedFiles,

    // Selection
    selectedFile,
    selectFile,
    fileContent,
    isLoadingContent,
    contentError,
    loadFileContent,

    // File size validation
    fileSizeValid,
    fileSizeError,

    // Cost dialog
    showCostDialog,
    estimatedCost,
    currentBalance,
    hasSufficientBalance,
    purchaseUrl,
    isLoadingCost,
    costError,
    openCostDialog,
    closeCostDialog,

    // Analysis (legacy sync - keeping for fallback)
    analysisResult: legacyAnalysisResult,
    isAnalyzing: legacyIsAnalyzing,
    analysisError: legacyAnalysisError,
    tokensUsed: legacyTokensUsed,
    balanceRemaining,
    startAnalysis: legacyStartAnalysis,

    // Modifications
    modifiedSource,
    hasModifications,
    viewCodeChanges,
    backToResults,

    // Save
    isSaving,
    saveResult,
    saveError,
    saveModifiedEA,

    // View
    currentView
  } = useEAAssistant(token);

  // ═══════════════════════════════════════════════════════════════════════════
  // ASYNC ANALYSIS with SignalR (NEW)
  // ═══════════════════════════════════════════════════════════════════════════
  const {
    // State
    state: asyncState,
    isAnalyzing: asyncIsAnalyzing,
    isComplete: asyncIsComplete,
    isFailed: asyncIsFailed,
    error: asyncError,

    // Job info
    jobId,
    queuePosition,

    // Progress
    progress,
    stage,
    stageInfo,
    chunksCompleted,
    totalChunks,

    // Results
    result: asyncResult,
    tokensUsed: asyncTokensUsed,
    processingTimeMs,

    // File content (source code) - for passing to chat context
    fileContent: asyncFileContent,

    // Consent modal
    showConsentModal,
    analysisType,
    closeConsentModal,

    // P9-B: Duplicate detection
    showDuplicateModal,
    duplicateInfo,
    isCheckingHash,
    closeDuplicateModal,
    useExistingAnalysis,
    proceedWithReanalysis,

    // P9-C: MQL Library
    libraryItems,
    libraryGrouped,
    libraryByType,
    isLoadingLibrary,
    libraryError,
    loadLibrary,
    loadFromLibrary,

    // Actions
    requestAnalysis,
    startAnalysisWithConsent,
    cancelAnalysis,
    reset: resetAsyncAnalysis
  } = useEAAsyncAnalysis(token);

  // Use async analysis results when available, fallback to legacy
  const analysisResult = asyncResult || legacyAnalysisResult;
  const isAnalyzing = asyncIsAnalyzing || legacyIsAnalyzing;
  const analysisError = asyncError || legacyAnalysisError;
  const tokensUsed = asyncTokensUsed || legacyTokensUsed;

  /**
   * Handle "Analyze with Q.AI" button click
   * Now uses async analysis with consent modal first
   * P9-B: Includes hash check for duplicate detection
   */
  const handleAnalyzeClick = useCallback(async () => {
    if (!selectedFile) return;

    // Reset any previous async analysis state
    resetAsyncAnalysis();

    // Request analysis - this will show consent modal after loading content
    // Pass userId for P9-B hash check (duplicate detection)
    requestAnalysis(selectedFile, 'full', userId);
  }, [selectedFile, requestAnalysis, resetAsyncAnalysis, userId]);

  /**
   * Handle library item selection (P9-C)
   * Loads existing analysis instantly (0 tokens)
   */
  const handleLibrarySelect = useCallback((libraryItem) => {
    if (!libraryItem) return;

    // Reset any previous state
    resetAsyncAnalysis();

    // Load from library
    loadFromLibrary(libraryItem);
  }, [loadFromLibrary, resetAsyncAnalysis]);

  /**
   * Refresh library data (P9-C)
   */
  const handleLibraryRefresh = useCallback(() => {
    if (userId) {
      loadLibrary(userId);
    }
  }, [userId, loadLibrary]);

  /**
   * Handle consent confirmation - start the actual analysis
   */
  const handleConsentConfirm = useCallback(() => {
    startAnalysisWithConsent(true);
  }, [startAnalysisWithConsent]);

  /**
   * Handle "Ask Q about this EA" button - Opens drawer with initial prompt
   */
  const handleAskAboutEA = useCallback(() => {
    if (selectedFile) {
      setChatInitialPrompt(`Tell me about my EA "${selectedFile.name}" - what does it do and what trading strategy does it implement?`);
      setChatDrawerOpen(true);
    }
  }, [selectedFile]);

  /**
   * Handle "Ask Q.AI" from results view - Opens drawer with analysis context
   */
  const handleAskFromResults = useCallback(() => {
    if (selectedFile) {
      setChatInitialPrompt(`I just analyzed "${selectedFile.name}". Can you explain the key findings and what I should fix first?`);
      setChatDrawerOpen(true);
    }
  }, [selectedFile]);

  /**
   * Open chat drawer without initial prompt
   */
  const handleOpenChatDrawer = useCallback(() => {
    setChatInitialPrompt(null);
    setChatDrawerOpen(true);
  }, []);

  /**
   * Close chat drawer
   */
  const handleCloseChatDrawer = useCallback(() => {
    setChatDrawerOpen(false);
    setChatInitialPrompt(null);
  }, []);

  /**
   * Update analysis context when async analysis completes
   * IMPORTANT: Include file content so Q.AI can reference the actual source code
   */
  useEffect(() => {
    if (asyncIsComplete && asyncResult && selectedFile) {
      // Pass fileContent so Q.AI can reference actual code
      const context = buildEAContextFromAnalysis(selectedFile, asyncResult, asyncFileContent);
      console.log('[EAManagerTab] Built EA context for chat:', {
        fileName: context?.fileName,
        hasSummary: !!context?.summary,
        bugsCount: context?.bugs?.length || 0,
        improvementsCount: context?.improvements?.length || 0,
        hasSourceCode: !!context?.sourceCodeSnippet
      });
      setEaAnalysisContext(context);
    }
  }, [asyncIsComplete, asyncResult, selectedFile, asyncFileContent]);

  /**
   * Clear analysis context when file changes
   */
  useEffect(() => {
    setEaAnalysisContext(null);
  }, [selectedFile?.name]);

  /**
   * Render right panel content based on current view
   */
  const renderRightPanel = () => {
    // Async analysis in progress - show real-time progress panel
    if (asyncIsAnalyzing && asyncState !== ANALYSIS_STATE.AWAITING_CONSENT) {
      return (
        <EAProgressPanel
          file={selectedFile}
          state={asyncState}
          progress={progress}
          stage={stage}
          stageInfo={stageInfo}
          chunksCompleted={chunksCompleted}
          totalChunks={totalChunks}
          queuePosition={queuePosition}
          onCancel={cancelAnalysis}
        />
      );
    }

    // Legacy analyzing state (fallback)
    if (legacyIsAnalyzing || currentView === EA_VIEWS.ANALYZING) {
      return <AnalyzingPanel file={selectedFile} />;
    }

    // Diff view
    if (currentView === EA_VIEWS.DIFF && modifiedSource) {
      return (
        <EADiffViewer
          file={selectedFile}
          originalSource={fileContent?.main_source || ''}
          modifiedSource={modifiedSource}
          onBack={backToResults}
          onSave={saveModifiedEA}
          isSaving={isSaving}
          saveResult={saveResult}
          saveError={saveError}
        />
      );
    }

    // Results view (async completed OR legacy results)
    if ((asyncIsComplete && asyncResult) || (currentView === EA_VIEWS.RESULTS && analysisResult)) {
      return (
        <EAAnalysisResults
          file={selectedFile}
          analysis={analysisResult}
          tokensUsed={tokensUsed}
          balanceRemaining={balanceRemaining}
          processingTimeMs={processingTimeMs}
          onViewChanges={viewCodeChanges}
          onAskQuestion={handleAskFromResults}
          hasModifications={hasModifications}
          onNewAnalysis={resetAsyncAnalysis}
        />
      );
    }

    // File selected but not analyzed (or async failed - allow retry)
    if (selectedFile) {
      const displayError = asyncIsFailed
        ? asyncError?.message
        : (contentError || analysisError?.message);

      return (
        <FileDetailsPanel
          file={selectedFile}
          onAnalyze={handleAnalyzeClick}
          onAskQ={handleAskAboutEA}
          isLoadingContent={isLoadingContent || asyncState === ANALYSIS_STATE.LOADING_CONTENT}
          error={displayError}
        />
      );
    }

    // No file selected
    return (
      <EmptyStatePanel
        qbridgeOnline={qbridgeOnline}
        libraryItems={libraryItems}
        libraryGrouped={libraryGrouped}
        libraryByType={libraryByType}
        isLoadingLibrary={isLoadingLibrary}
        libraryError={libraryError}
        onLibrarySelect={handleLibrarySelect}
        onLibraryRefresh={handleLibraryRefresh}
      />
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        height: '100%',
        gap: 2,
        overflow: 'hidden'
      }}
    >
      {/* ════════════════════════════════════════════════════════════════════════
          LEFT PANEL - File List
          ════════════════════════════════════════════════════════════════════════ */}
      <Box
        sx={{
          width: { xs: '100%', md: '280px' },
          minWidth: { md: '240px' },
          maxWidth: { md: '320px' },
          flexShrink: 0,
          height: { xs: '40%', md: '100%' },
          minHeight: { xs: '200px', md: 'auto' }
        }}
      >
        <EAFileList
          files={files}
          summary={fileSummary}
          isLoading={isLoadingFiles}
          error={filesError}
          selectedFile={selectedFile}
          onSelectFile={selectFile}
          onRefresh={refreshFiles}
          qbridgeOnline={qbridgeOnline}
          analyzedFiles={analyzedFiles}
        />
      </Box>

      {/* ════════════════════════════════════════════════════════════════════════
          RIGHT PANEL - Content Area
          ════════════════════════════════════════════════════════════════════════ */}
      <Box
        sx={{
          flex: 1,
          height: { xs: '60%', md: '100%' },
          backgroundColor: '#0F0F14',
          borderRadius: '12px',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          overflow: 'hidden'
        }}
      >
        {renderRightPanel()}
      </Box>

      {/* ════════════════════════════════════════════════════════════════════════
          COST ESTIMATE DIALOG (Legacy)
          ════════════════════════════════════════════════════════════════════════ */}
      <CostEstimateDialog
        open={showCostDialog}
        onClose={closeCostDialog}
        onConfirm={legacyStartAnalysis}
        onBuyTokens={onBuyTokens}
        file={selectedFile}
        estimatedCost={estimatedCost}
        currentBalance={currentBalance || balance}
        hasSufficientBalance={hasSufficientBalance}
        purchaseUrl={purchaseUrl}
        isLoading={isLoadingCost}
        error={costError || fileSizeError}
      />

      {/* ════════════════════════════════════════════════════════════════════════
          CONSENT MODAL (NEW - Required before any analysis)
          ════════════════════════════════════════════════════════════════════════ */}
      <EAConsentModal
        open={showConsentModal}
        onClose={closeConsentModal}
        onConfirm={handleConsentConfirm}
        file={selectedFile}
        analysisType={analysisType}
        isLoading={asyncState === ANALYSIS_STATE.SUBMITTING}
      />

      {/* ════════════════════════════════════════════════════════════════════════
          DUPLICATE EA MODAL (P9-B - Hash duplicate detection)
          ════════════════════════════════════════════════════════════════════════ */}
      <DuplicateEAModal
        open={showDuplicateModal}
        onClose={closeDuplicateModal}
        onUseExisting={useExistingAnalysis}
        onReanalyze={proceedWithReanalysis}
        duplicateInfo={duplicateInfo}
        file={selectedFile}
        isLoading={asyncState === ANALYSIS_STATE.LOADING_CONTENT}
      />

      {/* ════════════════════════════════════════════════════════════════════════
          EA CHAT DRAWER - Contextual Q.AI conversations
          ════════════════════════════════════════════════════════════════════════ */}
      <EAChatDrawer
        open={chatDrawerOpen}
        onClose={handleCloseChatDrawer}
        token={token}
        file={selectedFile}
        analysisContext={eaAnalysisContext}
        initialPrompt={chatInitialPrompt}
      />
    </Box>
  );
};

export default EAManagerTab;
